import { querytemplateApi } from '../../apis';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';
import { showFeedback } from '../../feedback/feedbackReducer';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useDispatch } from 'react-redux';

export const QUERY_TEMPLATE_KEY = 'query_templates';
export const QUERY_TEMPLATE_UPDATE_KEY = 'query_template_update';

export const useCreateQueryTemplate = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation, features } = useMe();
  const fourEyesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.FOUR_EYES_QUERY_TEMPLATES, features);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ name, content }: { name: string; content: string }) => {
      return querytemplateApi.createQueryTemplate({
        apiQueryTemplateUpdateRequest: {
          name,
          content,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_KEY, currentOrganisation?.id] });
      if (fourEyesEnabled) {
        dispatch(
          showFeedback({
            message: 'The changes are staged for review.',
            success: true,
          })
        );
      }
    },
  });
};

export const useUpdateQueryTemplate = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation, features } = useMe();
  const fourEyesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.FOUR_EYES_QUERY_TEMPLATES, features);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, name, content }: { id: number; handle: string; name: string; content: string }) => {
      return querytemplateApi.updateQueryTemplate({
        id,
        apiQueryTemplateUpdateRequest: {
          name,
          content,
        },
      });
    },
    onSuccess: (_, { handle }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_KEY, currentOrganisation?.id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_UPDATE_KEY, handle, currentOrganisation?.id] });
      if (fourEyesEnabled) {
        dispatch(
          showFeedback({
            message: 'The changes are staged for review.',
            success: true,
          })
        );
      }
    },
  });
};

export const useDeleteQueryTemplate = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation, features } = useMe();
  const fourEyesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.FOUR_EYES_QUERY_TEMPLATES, features);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id }: { id: number; handle: string }) => {
      return querytemplateApi.deleteQueryTemplate({ id });
    },
    onSuccess: (_, { handle }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_KEY, currentOrganisation?.id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_UPDATE_KEY, handle, currentOrganisation?.id] });
      if (fourEyesEnabled) {
        dispatch(
          showFeedback({
            message: 'The changes are staged for review.',
            success: true,
          })
        );
      }
    },
  });
};

export const useQueryTemplateUpdate = (enabled: boolean, handle?: string) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: [QUERY_TEMPLATE_UPDATE_KEY, handle, currentOrganisation?.id],
    queryFn: async () => (handle ? await querytemplateApi.getQueryTemplateUpdate({ handle }) : undefined),
    enabled: enabled,
  });
};
